import { useEffect, useRef, useState } from 'react';
import { resolveEntity } from '../../lib/command';
import './CommandBar.css';

// One slot of the chip bar: a labelled box that searches as you type and holds
// a chosen record.
//
// ⚠️ THIS IS THE FAST PATH, NOT THE FALLBACK. For somebody raising forty tasks
// a day, picking from a list is quicker than writing a sentence and far quicker
// than correcting one that was misread — there is no parsing step to be wrong.
// The natural-language box exists for the times a whole instruction is already
// in someone's head; this exists for the times it is not.
export default function EntityChip({
  label, type, value, onChange, placeholder, autoFocus = false, disabled = false,
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const text = q.trim();
    // ⚠️ DEBOUNCED, AND THE PREVIOUS REQUEST IS ABORTED. Without the abort,
    // responses race: a slow answer for "sha" can land after a fast one for
    // "sharma" and repopulate the list with the wrong candidates.
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const data = await resolveEntity({ type, q: text, signal: controller.signal });
        setOptions(data.candidates || []);
        setActive(0);
      } catch {
        // An aborted or failed lookup leaves the last list in place rather than
        // blanking it — an empty dropdown reads as "no such client".
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, type, open]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (opt) => {
    onChange(opt);
    setQ('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && options[active]) { e.preventDefault(); choose(options[active]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="cmd-chip" ref={boxRef}>
      <span className="cmd-chip-label">{label}</span>

      {value ? (
        <span className="cmd-chip-value">
          {value.label}
          {!disabled && (
            <button type="button" className="cmd-chip-clear" onClick={() => onChange(null)} aria-label={`Clear ${label}`}>
              ×
            </button>
          )}
        </span>
      ) : (
        <input
          className="cmd-chip-input"
          value={q}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder || `Search ${type}…`}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      )}

      {open && !value && (
        <ul className="cmd-chip-menu" role="listbox">
          {loading && <li className="cmd-chip-hint">Searching…</li>}
          {!loading && !options.length && <li className="cmd-chip-hint">No matches</li>}
          {options.map((o, i) => (
            <li key={o.id}>
              <button
                type="button"
                className={`cmd-chip-option${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o)}
              >
                <span className="cmd-chip-option-label">{o.label}</span>
                {o.meta?.clientCode && <span className="cmd-chip-option-meta">{o.meta.clientCode}</span>}
                {o.meta?.code && <span className="cmd-chip-option-meta">{o.meta.code}</span>}
                {/* Why this candidate is here. 'recent' is the boost that does
                    most of the work, and saying so out loud makes the ordering
                    explicable rather than magic. */}
                {o.method === 'alias_user' && <span className="cmd-chip-option-why">you usually mean this</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
